import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '@env/environment';
import { IUser } from 'app/core/models/model.model';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  #httpClient = inject(HttpClient);
  #user = signal<IUser | undefined>(undefined);

  get user(): IUser | undefined {
    return this.#user();
  }

  set user(user: IUser | undefined) {
    this.#user.set(user);
  }

  getRemainingAiImages(): number | undefined {
    return this.user?.remainingAiImages;
  }

  hasRemainingAiImages(required = 1): boolean {
    if (required <= 0) {
      return true;
    }

    const remaining = this.getRemainingAiImages();
    return remaining === undefined || remaining >= required;
  }

  async decrementRemainingAiImages(count = 1): Promise<void> {
    if (count <= 0) {
      return;
    }

    const currentUser = this.user;
    if (
      !currentUser ||
      !currentUser.id ||
      currentUser.remainingAiImages === undefined
    ) {
      return;
    }

    const remainingAiImages = Math.max(
      0,
      currentUser.remainingAiImages - count
    );
    const imagesAiGenerated = (currentUser.imagesAiGenerated ?? 0) + count;

    const updatedUser = await firstValueFrom(
      this.#httpClient.put<Partial<IUser>>(
        `${environment.apiUrl}/users/${currentUser.id}`,
        {
          remainingAiImages,
          imagesAiGenerated,
        }
      )
    );

    this.user = {
      ...currentUser,
      ...updatedUser,
      imagesAiGenerated:
        updatedUser.imagesAiGenerated === undefined
          ? imagesAiGenerated
          : updatedUser.imagesAiGenerated,
      remainingAiImages:
        updatedUser.remainingAiImages === undefined
          ? remainingAiImages
          : updatedUser.remainingAiImages,
    };
  }
}
